/**
 * #11 溯源履歷頁(公開,user 不必登入)
 *
 * 兩個 mode:
 *  1. Form mode — 預設,user 輸入批號 → 查詢
 *  2. Result mode — 查到對應燕屋 → 展示完整履歷
 *
 * URL param 支援:?batch=152034 → 進來自動 lookup
 */
import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { lookupBatch, type TraceabilityData } from "../api/traceability";
import { useUIStore } from "../store/uiStore";


export default function Traceability() {
  const navigate = useNavigate();
  const [batch, setBatch] = useState("");
  const [result, setResult] = useState<TraceabilityData | null>(null);
  const [verifiedBatch, setVerifiedBatch] = useState<number | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  // Auto-lookup from query param (e.g., ?batch=152034)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initial = params.get("batch");
    if (initial && /^\d+$/.test(initial)) {
      setBatch(initial);
      doLookup(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doLookup(b: string) {
    setSubmitting(true);
    setNotFound(false);
    try {
      const r = await lookupBatch(b);
      if (r.found && r.data) {
        setResult(r.data);
        setVerifiedBatch(r.batch || null);
      } else {
        setResult(null);
        setNotFound(true);
      }
    } catch {
      showToast("查詢連線異常,請稍後再試", "error");
    } finally {
      setSubmitting(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const b = batch.trim();
    if (!/^\d+$/.test(b)) {
      showToast("請輸入純數字批號", "error");
      return;
    }
    doLookup(b);
  }

  function reset() {
    setBatch("");
    setResult(null);
    setVerifiedBatch(null);
    setNotFound(false);
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      {/* Hero header */}
      <header className="bg-primary-dark text-white px-6 py-10 text-center">
        <div className="text-xs opacity-70 tracking-widest">PROVENANCE</div>
        <h1 className="text-2xl font-bold mt-2">專屬溯源履歷</h1>
        <div className="text-xs opacity-80 mt-2">從大馬原生態燕屋,到您的餐桌</div>
      </header>

      {/* Form mode */}
      {!result && (
        <div className="px-6 mt-8 max-w-md mx-auto">
          <div className="card space-y-4">
            <p className="text-sm text-muted leading-relaxed">
              請輸入您手中瓶身的批號(5 位數),系統將即時為您呈現該批燕窩的完整來源履歷。
            </p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="label" htmlFor="batch">瓶身批號</label>
                <input
                  id="batch"
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  className="input text-lg font-mono tracking-widest"
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                  placeholder="例:152034"
                  autoFocus
                />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full">
                {submitting ? "查詢中…" : "查詢溯源履歷"}
              </button>
            </form>
            {notFound && (
              <div className="text-sm text-coral border border-coral/30 rounded-lg p-3 bg-coral/5">
                查無此批號,請確認瓶身編號是否正確。
              </div>
            )}
          </div>
        </div>
      )}

      {/* Result mode */}
      {result && (
        <ResultView data={result} verifiedBatch={verifiedBatch} onReset={reset} />
      )}
    </div>
  );
}


// 空值判斷 — 文字空白 / 數字 0 視為「沒填」,該格就不顯示
const _t = (s?: string) => (s ?? "").trim();
const _hasT = (s?: string) => _t(s) !== "";
const _hasN = (n?: number) => typeof n === "number" && n > 0;

function ResultView({
  data,
  verifiedBatch,
  onReset,
}: {
  data: TraceabilityData;
  verifiedBatch: number | null;
  onReset: () => void;
}) {
  const loc = data.location;
  const gps = loc.gps;
  const bld = data.building;
  const cert = data.certification;
  const captions = data.captions_zh || [];
  const gallery = data.images.gallery || [];
  const narrative = data.narrative_zh || [];

  const hasGps = _hasN(gps.lat) && _hasN(gps.lng);
  const mapsUrl = `https://www.google.com/maps?q=${gps.lat},${gps.lng}`;
  const geoLine1 = [loc.country_zh, loc.state_zh].filter(_hasT).join(" ");
  const geoLine2 = [loc.district_zh, loc.district].filter(_hasT).join(" ");
  const hasGeo = _hasT(geoLine1) || _hasT(geoLine2);
  const hasBuildingStats =
    _hasN(bld.floors) || _hasN(bld.area_sqft) || _hasN(bld.founded_year) || _hasN(bld.harvesting_years);
  const showBuildingCard = _hasT(bld.description_zh) || hasBuildingStats;
  const showCertCard =
    _hasT(cert.authority_zh) || _hasT(cert.authority_full) || _hasT(cert.reviewer_zh) || _hasT(cert.date);

  return (
    <div className="space-y-5 mt-5">
      {/* Verification badge */}
      {verifiedBatch && (
        <div className="mx-6 px-5 py-4 bg-primary text-white rounded-2xl text-center">
          <div className="text-xs opacity-80">您手中的瓶身批號</div>
          <div className="text-xl font-mono tracking-widest mt-1">{verifiedBatch}</div>
          <div className="text-xs mt-2 opacity-90">
            <Check size={14} strokeWidth={2} className="inline -mt-0.5 mr-1" aria-hidden="true" />已驗證屬於本批
          </div>
        </div>
      )}

      {/* Hero image */}
      {_hasT(data.images.hero) && (
        <div className="px-6">
          <img
            src={data.images.hero}
            alt={data.headline_zh || "溯源履歷"}
            className="w-full aspect-video object-cover rounded-2xl"
            loading="lazy"
          />
        </div>
      )}

      {/* Headline */}
      {(_hasT(data.headline_en) || _hasT(data.headline_zh)) && (
        <div className="px-6 text-center">
          {_hasT(data.headline_en) && (
            <div className="text-xs text-muted tracking-widest uppercase">{data.headline_en}</div>
          )}
          {_hasT(data.headline_zh) && <h2 className="text-xl font-bold text-ink mt-2">{data.headline_zh}</h2>}
        </div>
      )}

      {/* House info card */}
      <div className="px-6">
        <div className="card space-y-2.5">
          <div className="text-xs text-muted font-medium mb-1">官方認證燕屋</div>
          {_hasT(data.house_id) && (
            <div className="flex items-baseline justify-between">
              <span className="text-muted text-sm">燕屋編號</span>
              <span className="text-ink font-mono font-bold">ID {data.house_id}</span>
            </div>
          )}
          {hasGeo && (
            <div className="flex items-baseline justify-between">
              <span className="text-muted text-sm">地理位置</span>
              <span className="text-ink text-sm text-right">
                {geoLine1}
                {_hasT(geoLine1) && _hasT(geoLine2) && <br />}
                {geoLine2}
              </span>
            </div>
          )}
          {hasGps && (
            <div className="flex items-baseline justify-between">
              <span className="text-muted text-sm">精確座標</span>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-sm font-mono underline"
              >
                {gps.lat}, {gps.lng}
              </a>
            </div>
          )}
          {(_hasN(data.batch_range.from) || _hasN(data.batch_range.to)) && (
            <div className="flex items-baseline justify-between">
              <span className="text-muted text-sm">瓶身批號範圍</span>
              <span className="text-ink font-mono">{data.batch_range.from}~{data.batch_range.to}</span>
            </div>
          )}
        </div>
      </div>

      {/* Building card */}
      {showBuildingCard && (
        <div className="px-6">
          <div className="card space-y-2.5">
            <div className="text-xs text-muted font-medium mb-1">專業生態建築</div>
            {_hasT(bld.description_zh) && <div className="text-sm text-ink">{bld.description_zh}</div>}
            {hasBuildingStats && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                {_hasN(bld.floors) && <Stat label="建築層數" value={`${bld.floors} 層式`} />}
                {_hasN(bld.area_sqft) && <Stat label="占地面積" value={`${bld.area_sqft.toLocaleString()} 平方呎`} />}
                {_hasN(bld.founded_year) && <Stat label="創始年份" value={`${bld.founded_year}`} />}
                {_hasN(bld.harvesting_years) && <Stat label="採集經歷" value={`${bld.harvesting_years} 年以上`} />}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Certification card */}
      {showCertCard && (
        <div className="px-6">
          <div className="card space-y-2.5">
            <div className="text-xs text-muted font-medium mb-1">官方核准</div>
            {(_hasT(cert.authority_zh) || _hasT(cert.authority_full)) && (
              <div className="flex items-baseline justify-between">
                <span className="text-muted text-sm">認證機構</span>
                <span className="text-ink text-sm text-right">
                  {cert.authority_zh}
                  {_hasT(cert.authority_zh) && _hasT(cert.authority_full) && <br />}
                  {_hasT(cert.authority_full) && <span className="text-xs text-muted">{cert.authority_full}</span>}
                </span>
              </div>
            )}
            {_hasT(cert.reviewer_zh) && (
              <div className="flex items-baseline justify-between">
                <span className="text-muted text-sm">審核單位</span>
                <span className="text-ink text-sm">{cert.reviewer_zh}</span>
              </div>
            )}
            {_hasT(cert.date) && (
              <div className="flex items-baseline justify-between">
                <span className="text-muted text-sm">最後更新</span>
                <span className="text-ink text-sm font-mono">{cert.date}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <div className="px-6">
          <div className="text-xs text-muted font-medium mb-3">工藝實景</div>
          <div className="space-y-4">
            {gallery.map((url, i) => (
              <figure key={i} className="space-y-2">
                <img src={url} alt={captions[i] || ""} className="w-full aspect-[3/2] object-cover rounded-2xl" loading="lazy" />
                {captions[i] && (
                  <figcaption className="text-xs text-muted text-center px-2">{captions[i]}</figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      )}

      {/* Narrative */}
      {narrative.length > 0 && (
        <div className="px-6">
          <div className="card space-y-3">
            {narrative.map((p, i) => (
              <p key={i} className="text-sm text-ink leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      )}

      {/* Bottom actions */}
      <div className="px-6 space-y-2 pt-2">
        <button onClick={onReset} className="btn-secondary w-full">查詢其他批號</button>
      </div>
    </div>
  );
}


function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center bg-cream rounded-lg py-2">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-sm font-medium text-ink mt-0.5">{value}</div>
    </div>
  );
}
